/**
 * Cart reservation countdown.
 *
 * - One shared ticker for every <cart-reservation-timer> on the page (drawer + cart page), so
 *   section rerenders never stack up intervals.
 * - State is an absolute expiry timestamp in localStorage, not a counter, so refreshes and
 *   navigation keep the remaining time.
 * - Starts when the cart first becomes non-empty, restarts when the item count increases,
 *   clears when the cart becomes empty. Never touches the cart itself.
 * - Copy is server-rendered into data attributes from the locale files; this script only
 *   swaps the __TIME__ token.
 */

const STORAGE_KEY = 'rm-cart-reservation';
const DURATION_MS = 5 * 60 * 1000;
const TIME_TOKEN = '__TIME__';
const TAG_NAME = 'cart-reservation-timer';
const CART_UPDATE_EVENT = 'cart:update';

/** @typedef {{ expiresAt: number, itemCount: number }} ReservationState */

const storage = {
  /** @returns {ReservationState | null} */
  read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.expiresAt !== 'number' || typeof parsed.itemCount !== 'number') return null;
      return parsed;
    } catch {
      return null;
    }
  },
  /** @param {ReservationState} state */
  write(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage unavailable (private mode, quota) - countdown simply won't persist */
    }
  },
  clear() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  },
};

/** @type {number | null} */
let tickerHandle = null;

/**
 * Formats milliseconds as MM:SS (minutes can exceed 59, but never will here).
 * @param {number} ms
 */
function formatRemaining(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** @returns {NodeListOf<CartReservationTimer>} */
function allTimers() {
  return /** @type {NodeListOf<CartReservationTimer>} */ (document.querySelectorAll(TAG_NAME));
}

/**
 * Renders the current state into every timer element and decides whether the ticker keeps running.
 */
function render() {
  const state = storage.read();
  const remaining = state ? state.expiresAt - Date.now() : 0;

  for (const timer of allTimers()) {
    timer.render(state, remaining);
  }

  // Nothing left to count: stop the ticker. It restarts on the next sync() that needs it.
  if (!state || remaining <= 0) stopTicker();
}

function startTicker() {
  if (tickerHandle !== null) return;
  const schedule = () => {
    // Align to the next wall-clock second so the display never skips a second.
    tickerHandle = window.setTimeout(() => {
      tickerHandle = null;
      render();
      if (storage.read() && tickerHandle === null && !isExpired()) schedule();
    }, 1000 - (Date.now() % 1000));
  };
  schedule();
}

function stopTicker() {
  if (tickerHandle === null) return;
  window.clearTimeout(tickerHandle);
  tickerHandle = null;
}

function isExpired() {
  const state = storage.read();
  return !state || state.expiresAt - Date.now() <= 0;
}

/**
 * Reconciles stored state with the cart's current item count.
 * @param {number} itemCount
 */
function sync(itemCount) {
  if (!Number.isFinite(itemCount)) return;

  if (itemCount <= 0) {
    storage.clear();
    stopTicker();
    render();
    return;
  }

  const current = storage.read();

  if (!current || itemCount > current.itemCount) {
    // First item, or another product/unit was added: (re)start the five minutes.
    storage.write({ expiresAt: Date.now() + DURATION_MS, itemCount });
  } else if (itemCount !== current.itemCount) {
    // Quantity went down but the cart is not empty: keep the existing expiry.
    storage.write({ ...current, itemCount });
  }

  render();
  if (!isExpired()) startTicker();
}

class CartReservationTimer extends HTMLElement {
  static get observedAttributes() {
    return ['data-item-count'];
  }

  /** Exposed for QA: true while the single shared ticker is scheduled. */
  static get ticking() {
    return tickerHandle !== null;
  }

  static get storageKey() {
    return STORAGE_KEY;
  }

  connectedCallback() {
    sync(this.itemCount);
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected) return;
    sync(this.itemCount);
  }

  get itemCount() {
    return Number(this.dataset.itemCount ?? 0);
  }

  /**
   * @param {ReservationState | null} state
   * @param {number} remaining - milliseconds left
   */
  render(state, remaining) {
    const text = this.querySelector('[data-timer-text]');
    if (!text) return;

    if (!state || this.itemCount <= 0) {
      this.hidden = true;
      return;
    }

    if (remaining <= 0) {
      text.textContent = this.dataset.expiredMessage ?? '';
      this.dataset.state = 'expired';
    } else {
      text.textContent = (this.dataset.message ?? TIME_TOKEN).replace(TIME_TOKEN, formatRemaining(remaining));
      this.dataset.state = 'active';
    }

    this.dataset.ready = '';
    this.hidden = false;
  }
}

if (!customElements.get(TAG_NAME)) {
  customElements.define(TAG_NAME, CartReservationTimer);
}

/**
 * Cart events fire before the section morph, and also when the timer element is not on the page
 * (e.g. the drawer is showing its empty state). The cart object on the event carries the new
 * total item count.
 */
document.addEventListener(CART_UPDATE_EVENT, (event) => {
  const detail = /** @type {CustomEvent & { detail?: { resource?: { item_count?: number } } }} */ (event).detail;
  const itemCount = detail?.resource?.item_count;
  if (typeof itemCount === 'number') sync(itemCount);
});

// Re-render when the tab becomes visible again (timers are throttled in background tabs).
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  render();
  if (storage.read() && !isExpired()) startTicker();
});

// Back/forward cache restores: re-sync from the rendered item counts.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  for (const timer of allTimers()) sync(timer.itemCount);
});

// Keep multiple tabs consistent.
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY && event.key !== null) return;
  render();
  if (storage.read() && !isExpired()) startTicker();
});
